/**
 * Copyright (C) 2024-present Puter Technologies Inc.
 *
 * This file is part of Puter.
 *
 * Puter is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

const nn = {
  name: 'Norsk Nynorsk',
  english_name: 'Norwegian Nynorsk',
  code: 'nn',
  dictionary: {
    access_granted_to: 'Tilgang gjeven til',
    add_existing_account: 'Legg til eksisterande konto',
    all_fields_required: 'Alle felt er obligatoriske.',
    apply: 'Bruk',
    ascending: 'Stigande',
    background: 'Bakgrunn',
    browse: 'Bla gjennom',
    cancel: 'Avbryt',
    center: 'Sentrer',
    change_desktop_background: 'Endre skrivebordsbakgrunn…',
    change_language: 'Endre språk',
    change_password: 'Endre passord',
    change_username: 'Endre brukarnamn',
    close_all_windows: 'Lukk alle vindauge',
    color: 'Farge',
    confirm_account_for_free_referral_storage_c2a:
      'Opprett ein konto og stadfest e-postadressa di for å få 1 GB gratis lagringsplass. Vennen din vil òg få 1 GB gratis lagringsplass.',
    confirm_new_password: 'Stadfest nytt passord',
    contact_us: 'Kontakt oss',
    contain: 'Inneheld',
    continue: 'Hald fram',
    copy: 'Kopier',
    copy_link: 'Kopier lenkje',
    copying: 'Kopierer',
    cover: 'Dekk',
    create_account: 'Opprett konto',
    create_free_account: 'Opprett gratis konto',
    create_shortcut: 'Opprett snarveg',
    current_password: 'Noeverande passord',
    cut: 'Klipp ut',
    date_modified: 'Endra dato',
    delete: 'Slett',
    delete_permanently: 'Slett permanent',
    deploy_as_app: 'Distribuer som app',
    descending: 'Synkande',
    desktop_background_fit: 'Tilpass',
    dir_published_as_website: '%strong% er publisert på:',
    disassociate_dir: 'Fjern tilknyting frå mappe',
    download: 'Last ned',
    downloading: 'Lastar ned',
    email: 'E-post',
    email_or_username: 'E-post eller brukarnamn',
    empty_trash: 'Tøm papirkorg',
    empty_trash_confirmation: 'Er du sikker på at du vil slette alt i papirkorga permanent?',
    emptying_trash: 'Tømmer papirkorg…',
    feedback: 'Tilbakemelding',
    feedback_c2a:
      'Vennligst bruk skjemaet nedanfor for å sende oss din tilbakemelding, kommentarar og feilrapportar.',
    feedback_sent_confirmation:
      'Takk for at du kontakta oss. Om du har ein e-post knytt til kontoen din, vil du høyre frå oss så snart som mogleg.',
    forgot_pass_c2a: 'Gløymt passord?',
    from: 'Frå',
    general: 'Generelt',
    get_a_copy_of_on_puter: "Få ein kopi av '%%' på Puter.com!",
    get_copy_link: 'Få kopilenkje',
    hide_all_windows: 'Skjul alle vindauge',
    html_document: 'HTML-dokument',
    image: 'Bilete',
    invite_link: 'Invitasjonslenkje',
    items_in_trash_cannot_be_renamed:
      'Dette elementet kan ikkje omdøypast fordi det er i papirkorga. For å omdøype dette elementet, dra det først ut av papirkorga.',
    jpeg_image: 'JPEG-bilete',
    keep_in_taskbar: 'Behald i oppgåvelinja',
    log_in: 'Logg inn',
    log_out: 'Logg ut',
    move: 'Flytt',
    moving_file: 'Flyttar %%',
    my_websites: 'Mine nettstader',
    name: 'Namn',
    name_cannot_be_empty: 'Namn kan ikkje vere tomt.',
    name_cannot_contain_double_period: "Namn kan ikkje innehalde '..'.",
    name_cannot_contain_period: "Namn kan ikkje innehalde '.'-teiknet.",
    name_cannot_contain_slash: "Namn kan ikkje innehalde '/'-teiknet.",
    name_must_be_string: 'Namn må vere ein streng.',
    name_too_long: 'Namn kan ikkje vere lengre enn %% teikn.',
    new: 'Ny',
    new_folder: 'Ny mappe',
    new_password: 'Nytt passord',
    new_username: 'Nytt brukarnamn',
    no_dir_associated_with_site: 'Ingen mappe er tilknytt denne adressa.',
    no_websites_published: 'Du har ikkje publisert nokre nettstader enno.',
    ok: 'OK',
    open: 'Opne',
    open_in_new_tab: 'Opne i ny fane',
    open_in_new_window: 'Opne i nytt vindauge',
    open_with: 'Opne med',
    password: 'Passord',
    password_changed: 'Passord endra.',
    passwords_do_not_match: '`Nytt passord` og `Stadfest nytt passord` stemmer ikkje overeins.',
    paste: 'Lim inn',
    paste_into_folder: 'Lim inn i mappe',
    pick_name_for_website: 'Vel eit namn for nettstaden din:',
    picture: 'Bilete',
    powered_by_puter_js: 'Dreve av {{link=docs}}Puter.js{{/link}}',
    preparing: 'Førebur…',
    preparing_for_upload: 'Førebur opplasting…',
    properties: 'Eigenskapar',
    publish: 'Publiser',
    publish_as_website: 'Publiser som nettstad',
    recent: 'Nyleg',
    recover_password: 'Gjenopprett passord',
    refer_friends_c2a:
      'Få 1 GB for kvar ven som opprettar og stadfestar ein konto på Puter. Vennen din får òg 1 GB.',
    refer_friends_social_media_c2a: 'Få 1 GB gratis lagringsplass på Puter.com!',
    refresh: 'Oppdater',
    release_address_confirmation: 'Er du sikker på at du vil sleppe denne adressa?',
    remove_from_taskbar: 'Fjern frå oppgåvelinja',
    rename: 'Gje nytt namn',
    repeat: 'Gjenta',
    resend_confirmation_code: 'Send stadfestingskoden på nytt',
    restore: 'Gjenopprett',
    save_account_to_get_copy_link: 'Vennligst opprett ein konto for å halde fram.',
    save_account_to_publish: 'Vennligst opprett ein konto for å halde fram.',
    save_session_c2a: 'Opprett ein konto for å lagre gjeldande økt og unngå å miste arbeidet ditt.',
    scan_qr_c2a: 'Skann koden nedanfor for å logge inn på denne økta frå andre einingar',
    select: 'Vel',
    select_color: 'Vel farge…',
    send: 'Send',
    send_password_recovery_email: 'Send e-post for gjenoppretting av passord',
    session_saved: 'Takk for at du oppretta ein konto. Denne økta er lagra.',
    set_new_password: 'Set nytt passord',
    share_to: 'Del',
    show_all_windows: 'Vis alle vindauge',
    show_hidden: 'Vis skjulte',
    sign_in_with_puter: 'Logg inn med Puter',
    sign_up: 'Registrer deg',
    signing_in: 'Logger inn…',
    size: 'Storleik',
    sort_by: 'Sorter etter',
    start: 'Start',
    taking_longer_than_usual: 'Dette tar litt lengre tid enn vanleg. Vennligst vent...',
    text_document: 'Tekstdokument',
    tos_fineprint:
      "Ved å klikke på 'Opprett gratis konto' godtek du Puters {{link=terms}}tenestevilkår{{/link}} og {{link=privacy}}personvernpolitikk{{/link}}.",
    trash: 'Papirkorg',
    type: 'Type',
    undo: 'Angra',
    unzip: 'Pakk ut',
    upload: 'Last opp',
    upload_here: 'Last opp her',
    username: 'Brukarnamn',
    username_changed: 'Brukarnamn oppdatert.',
    versions: 'Versjonar',
    yes_release_it: 'Ja, slepp den',
    you_have_been_referred_to_puter_by_a_friend: 'Du har blitt referert til Puter av ein ven!',
    zip: 'Zip',

    // Updated translations for previously missing keys
    about: 'Om', // In English: "About"
    account: 'Konto', // In English: "Account"
    account_password: 'Stadfest kontopassord', // In English: "Verify Account Password"
    allow: 'Tillat', // In English: "Allow"
    associated_websites: 'Tilknytte nettstader', // In English: "Associated Websites"
    auto_arrange: 'Auto-ordne', // In English: "Auto Arrange"
    change: 'Endre', // In English: "Change"
    change_always_open_with: 'Vil du alltid opne denne filtypen med', // In English: "Do you want to always open this type of file with"
    change_email: 'Endre e-post', // In English: "Change Email"
    change_ui_colors: 'Endre fargar på brukargrensesnitt', // In English: "Change UI Colors"
    clock_visibility: 'Synlegheit for klokke', // In English: "Clock Visibility"
    close: 'Lukk', // In English: "Close"
    close_all_windows_confirm: 'Er du sikker på at du vil lukke alle vindauge?', // In English: "Are you sure you want to close all windows?"
    close_all_windows_and_log_out: 'Lukk vindauge og logg ut', // In English: "Close Windows and Log Out"
    confirm: 'Stadfest', // In English: "Confirm"
    confirm_2fa_setup: 'Eg har lagt til koden i autentikatorappen min', // In English: "I have added the code to my authenticator app"
    confirm_2fa_recovery: 'Eg har lagra gjenopprettingskodane mine på ein trygg stad', // In English: "I have saved my recovery codes in a secure location"
    confirm_code_generic_incorrect: 'Feil kode.', // In English: "Incorrect Code."
    confirm_code_generic_too_many_requests: 'For mange førespurnader. Vennligst vent nokre minutt.', // In English: "Too many requests. Please wait a few minutes."
    confirm_code_generic_submit: 'Send inn kode', // In English: "Submit Code"
    confirm_code_generic_try_again: 'Prøv igjen', // In English: "Try Again"
    confirm_code_generic_title: 'Skriv inn stadfestingskode', // In English: "Enter Confirmation Code"
    confirm_code_2fa_instruction: 'Skriv inn den 6-sifra koden frå autentikatorappen din.', // In English: "Enter the 6-digit code from your authenticator app."
    confirm_code_2fa_submit_btn: 'Send inn', // In English: "Submit"
    confirm_code_2fa_title: 'Skriv inn 2FA-kode', // In English: "Enter 2FA Code"
    confirm_delete_multiple_items: 'Er du sikker på at du vil slette desse elementa permanent?', // In English: "Are you sure you want to permanently delete these items?"
    confirm_delete_single_item: 'Vil du slette dette elementet permanent?', // In English: "Do you want to permanently delete this item?"
    confirm_open_apps_log_out: 'Du har opne appar. Er du sikker på at du vil logge ut?', // In English: "You have open apps. Are you sure you want to log out?"
    confirm_delete_user:
      'Er du sikker på at du vil slette kontoen din? Alle filene og dataa dine vil bli sletta permanent. Denne handlinga kan ikkje angrast.', // In English: "Are you sure you want to delete your account? All your files and data will be permanently deleted. This action cannot be undone."
    confirm_delete_user_title: 'Slette konto?', // In English: "Delete Account?"
    confirm_session_revoke: 'Er du sikker på at du vil trekkje tilbake denne økta?', // In English: "Are you sure you want to revoke this session?"
    confirm_your_email_address: 'Stadfest e-postadressa di', // In English: "Confirm Your Email Address"
    contact_us_verification_required: 'Du må ha ein stadfesta e-postadresse for å bruke dette.', // In English: "You must have a verified email address to use this."
    copying_file: 'Kopierer %%', // In English: "Copying %%"
    credits: 'Bidragsytarar', // In English: "Credits"
    clock: 'Klokke', // In English: "Clock"
    clock_visible_hide: 'Skjul - Alltid skjult', // In English: "Hide - Always hidden"
    clock_visible_show: 'Vis - Alltid synleg', // In English: "Show - Always visible"
    clock_visible_auto: 'Auto - Standard, synleg berre i fullskjermmodus.', // In English: "Auto - Default, visible only in full-screen mode."
    close_all: 'Lukk alle', // In English: "Close All"
    created: 'Oppretta', // In English: "Created"
    default: 'Standard', // In English: "Default"
    delete_account: 'Slett konto', // In English: "Delete Account"
    deleting_file: 'Slettar %%', // In English: "Deleting %%"
    desktop: 'Skrivebord', // In English: "Desktop"
    developers: 'Utviklarar', // In English: "Developers"
    disable_2fa: 'Deaktiver 2FA', // In English: "Disable 2FA"
    disable_2fa_confirm: 'Er du sikker på at du vil deaktivere 2FA?', // In English: "Are you sure you want to disable 2FA?"
    disable_2fa_instructions: 'Skriv inn passordet ditt for å deaktivere 2FA.', // In English: "Enter your password to disable 2FA."
    documents: 'Dokument', // In English: "Documents"
    dont_allow: 'Ikkje tillat', // In English: "Don't Allow"
    download_file: 'Last ned fil', // In English: "Download File"
    email_change_confirmation_sent:
      'Ein stadfestings-e-post er sendt til den nye e-postadressa di. Vennligst sjekk innboksen og følg instruksjonane for å fullføre prosessen.', // In English: "A confirmation email has been sent to your new email address. Please check your inbox and follow the instructions to complete the process."
    email_invalid: 'E-postadressa er ugyldig.', // In English: "Email is invalid."
    email_required: 'E-post er krevd.', // In English: "Email is required."
    enable_2fa: 'Aktiver 2FA', // In English: "Enable 2FA"
    end_hard: 'Tvangslutt', // In English: "End Hard"
    end_process_force_confirm: 'Er du sikker på at du vil tvinge avslutting av denne prosessen?', // In English: "Are you sure you want to force-quit this process?"
    end_soft: 'Avslutt normalt', // In English: "End Soft"
    enlarged_qr_code: 'Forstørra QR-kode', // In English: "Enlarged QR Code"
    enter_password_to_confirm_delete_user: 'Skriv inn passordet ditt for å stadfeste kontoen.', // In English: "Enter your password to confirm account deletion"
    error_message_is_missing: 'Feilmelding manglar.', // In English: "Error message is missing."
    error_unknown_cause: 'Ein ukjent feil oppstod.', // In English: "An unknown error occurred."
    error_uploading_files: 'Klarte ikkje å laste opp filer.', // In English: "Failed to upload files"
    favorites: 'Favorittar', // In English: "Favorites"
    fit: 'Fit',
    folder: 'Mappe', // In English: "Folder"
    force_quit: 'Tving avslutt',
    // In English: "Force Quit"
    home: 'Heim',
    // In English: "Home"
    hue: 'Fargetone',
    incorrect_password: 'Feil passord',
    item: 'Element',
    language: 'Språk',
    license: 'Lisens',
    lightness: 'Lys',
    link_copied: 'Lenkja kopiert',
    loading: 'Lastar',
    log_into_another_account_anyway: 'Logg inn med ein annan konto likevel',
    looks_good: 'Ser bra ut!',
    manage_sessions: 'Handter økter',
    modified: 'Endra',
    new_email: 'Ny e-post',
    no: 'Nei',
    original_name: 'Orgininalt namn',
    original_path: 'Original sti',
    oss_code_and_content: 'Programvare med open kjeldekode og innhald',
    password_recovery_rate_limit:
      'Du har nådd grensa vår for frekvens; vennligst vent nokre minutt. For å unngå dette i framtida, ikkje last inn sida på nytt for mange gonger.',
    password_recovery_token_invalid:
      'Denne gjenopprettingskoden for passord er ikkje lenger gyldig.',
    password_recovery_unknown_error: 'Ein ukjent feil oppstod. Vennligst prøv igjen seinare.',
    password_required: 'Passord er krevd.',
    password_strength_error:
      'Passordet må vere minst 8 teikn langt og innehalde minst éin stor bokstav, éin liten bokstav, éin tal og éin spesialteikn.',
    path: 'Sti',
    personalization: 'Personleggjering',
    pictures: 'Bilete',
    plural_suffix: 'ar',
    print: 'Skriv ut',
    privacy: 'Personvern',
    proceed_to_login: 'Fortsett til innlogging',
    proceed_with_account_deletion: 'Fortsett med sletting av konto',
    process_status_initializing: 'Initialiserer',
    process_status_running: 'Køyrer',
    process_type_app: 'App',
    process_type_init: 'Init',
    process_type_ui: 'Brukargrensesn',
    public: 'Offentleg',
    puter_description:
      'Puter er ein personleg skyteknologi med fokus på personvern, der du kan lagre alle filene, appane og spela dine på ein sikker stad, tilgjengeleg frå overalt til ei kvar tid.',
    reading: 'Leser %strong%',
    writing: 'Skriv %strong%',
    recommended: 'Anbefalt',
    replace: 'Erstatt',
    replace_all: 'Erstatt alle',
    reset_colors: 'Nullstill fargar',
    restart_puter_confirm: 'Er du sikker på at du vil starte Puter på nytt?',
    save: 'Lagre',
    saturation: 'Metting',
    save_account: 'Lagre konto',
    save_session: 'Lagre økt',
    scan_qr_2fa: 'Skann QR-koden med autentikatorappen din',
    scan_qr_generic: 'Skann denne QR-koden med telefonen eller ei anna eining',
    search: 'Søk',
    seconds: 'sekund',
    security: 'Tryggleik',
    selected: 'valt',
    sessions: 'Økter',
    settings: 'Innstillingar',
    share: 'Del',
    share_with: 'Del med:',
    shortcut_to: 'Snarveg til',
    skip: 'Hopp over',
    something_went_wrong: 'Noko gjekk galt.',
    status: 'Status',
    storage_usage: 'Lagringsbruk',
    storage_puter_used: 'brukt av Puter',
    task_manager: 'Oppgavehandsaming',
    taskmgr_header_name: 'Namn',
    taskmgr_header_status: 'Status',
    taskmgr_header_type: 'Type',
    terms: 'Vilkår',
    transparency: 'Gjennomsikt',
    two_factor: 'Tofaktorautentisering',
    two_factor_disabled: '2FA deaktivert',
    two_factor_enabled: '2FA aktivert',
    type_confirm_to_delete_account: "Skriv inn 'konfirmer' for å slette kontoen din.",
    ui_colors: 'Fargar på brukargrensesn',
    ui_manage_sessions: 'Øktbehandler',
    ui_revoke: 'Trekk tilbake',
    unlimited: 'Ubegrensa',
    unzipping: 'Pakkar ut %strong%',
    usage: 'Bruk',
    username_required: 'Brukarnamn er krevd.',
    videos: 'Videoar',
    visibility: 'Synlegheit',
    yes: 'Ja',
    sequencing: 'Sekvenserar %strong%',
    zipping: 'Zippar %strong%',
    setup2fa_1_step_heading: 'Opne autentikatorappen din',
    setup2fa_1_instructions: `
                Du kan bruke ein autentikatorapp som støttar Time-based One-Time Password (TOTP)-protokollen.
                Det finst mange å velje mellom, men om du er usikker,
                er <a target="_blank" href="https://authy.com/download">Authy</a>
                eit godt valg for Android og iOS.
                `,
    setup2fa_2_step_heading: 'Skann QR-koden',
    setup2fa_3_step_heading: 'Skriv inn den 6-sifra koden',
    setup2fa_4_step_heading: 'Kopier gjenopprettingskodane dine',
    setup2fa_4_instructions: `
                Desse gjenopprettingskodane er den einaste måten å få tilgang til kontoen din om du mistar telefonen eller ikkje kan bruke autentikatorappen.
                Pass på å lagre dei på ein trygg stad.
                `,
    setup2fa_5_step_heading: 'Stadfest 2FA-oppsett',
    setup2fa_5_confirmation_1: 'Eg har lagra gjenopprettingskodane mine på ein trygg stad',
    setup2fa_5_confirmation_2: 'Eg er klar til å aktivere 2FA',
    setup2fa_5_button: 'Aktiver 2FA',
    login2fa_otp_title: 'Skriv inn 2FA-kode',
    login2fa_otp_instructions: 'Skriv inn den 6-sifra koden frå autentikatorappen din.',
    login2fa_recovery_title: 'Skriv inn ein gjenopprettingskode',
    login2fa_recovery_instructions:
      'Skriv inn ein av gjenopprettingskodane dine for å få tilgang til kontoen din.',
    login2fa_use_recovery_code: 'Bruk ein gjenopprettingskode',
    login2fa_recovery_back: 'Tilbake',
    login2fa_recovery_placeholder: 'XXXXXXXX',
    Editor: 'Redigerar',
    Viewer: 'Visar',
    'People with access': 'Personar med tilgang',
    'Share With…': 'Del med…',
    Owner: 'Eigar',
    "You can't share with yourself.": 'Du kan ikkje dele med deg sjølv.',
    'This user already has access to this item':
      'Denne brukaren har allereie tilgang til dette elementet',
    'billing.change_payment_method': 'Endre',
    'billing.cancel': 'Avbryt',
    'billing.download_invoice': 'Last ned faktura',
    'billing.payment_method': 'Betalingsmåte',
    'billing.payment_method_updated': 'Betalingsmåte oppdatert!',
    'billing.confirm_payment_method': 'Stadfest billingsmøte',
    'billing.payment_history': 'Betalinghistorikk',
    'billing.refunded': 'Refundert',
    'billing.paid': 'Betalt',
    'billing.ok': 'oK',
    'billing.resume_subscription': 'Gjenoppta abonnement',
    'billing.subscription_cancelled': 'Abonnementet ditt er kansellert.',
    'billing.subscription_cancelled_description':
      'Du vil framleis ha tilgang til abonnementet ditt fram til slutten av denne faktureringsperioden.',
    'billing.offering.free': 'Gratis',
    'billing.offering.pro': 'Profesjonell',
    'billing.offering.professional': 'Profesjonell',
    'billing.offering.business': 'Bedrift',
    'billing.cloud_storage': 'Skylagring',
    'billing.ai_access': 'AI-tilgang',
    'billing.bandwidth': 'Båndbredde',
    'billing.apps_and_games': 'Appar og spel',
    'billing.upgrade_to_pro': 'Oppgrader til %strong%',
    'billing.switch_to': 'Bytt til %strong%',
    'billing.payment_setup': 'Oppsett av betaling',
    'billing.back': 'ack',
    'billing.you_are_now_subscribed_to': 'Du er no abonnent på %strong%-nivået.',
    'billing.you_are_now_subscribed_to_without_tier': 'Du er no abonnent',
    'billing.subscription_cancellation_confirmation':
      'Er du sikker på at du vil kansellere abonnementet ditt?',
    'billing.subscription_setup': 'Abonnementsoppsett',
    'billing.cancel_it': 'Kanseller det',
    'billing.keep_it': 'Behald det',
    'billing.subscription_resumed': 'Ditt %strong%-abonnement er gjenopptatt!',
    'billing.upgrade_now': 'Oppgrader no', // In English: "Upgrade Now"
    'billing.upgrade': 'Oppgrader', // In English: "Upgrade"
    'billing.currently_on_free_plan': 'Du er no på gratisplanen.', // In English: "You are currently on the free plan."
    'billing.download_receipt': 'Last ned kvittering', // In English: "Download Receipt"
    'billing.subscription_check_error':
      'Ein feil oppstod under kontroll av abonnementsstatusen din.', // In English: "A problem occurred while checking your subscription status."
    'billing.email_confirmation_needed':
      'E-postadressa di er ikkje stadfesta. Vi sender deg ein kode for å stadfeste no.', // In English: "Your email has not been confirmed. We'll send you a code to confirm it now."
    'billing.sub_cancelled_but_valid_until':
      'Du har kansellert abonnementet ditt, og det vil automatisk bytte til gratisnivået ved slutten av faktureringsperioden. Du vil ikkje bli belast igjen med mindre du abonnerer på nytt.', // In English: "You have cancelled your subscription and it will automatically switch to the free tier at the end of the billing period. You will not be charged again unless you re-subscribe."
    'billing.current_plan_until_end_of_period':
      'Din gjeldande plan fram til slutten av denne faktureringsperioden.', // In English: "Your current plan until the end of this billing period."
    'billing.current_plan': 'Gjeldande plan', // In English: "Current plan"
    'billing.cancelled_subscription_tier': 'Kansellert abonnement (%%)', // In English: "Cancelled Subscription (%%)"
    'billing.manage': 'Handter', // In English: "Manage"
    'billing.limited': 'Begrensa', // In English: "Limited"
    'billing.expanded': 'Utvida', // In English: "Expanded"
    'billing.accelerated': 'Akselerert', // In English: "Accelerated"
    'billing.enjoy_msg': 'Nyt %% skylagring pluss andre fordelar.', // In English: "Enjoy %% of Cloud Storage plus other benefits."

    choose_publishing_option: 'Vel korleis du vil publisere nettsida di:', // "Original English: Choose how you want to publish your website:"
    create_desktop_shortcut: 'Lag snarveg (Skrivebord)', // "Original English: Create Shortcut (Desktop)"
    create_desktop_shortcut_s: 'Lag snarvegar (Skrivebord)', // "Original English: Create Shortcuts (Desktop)"
    create_shortcut_s: 'Lag snarvegar', // "Original English: Create Shortcuts"
    minimize: 'Minimer', // "Original English: Minimize"
    reload_app: 'Last inn app på nytt', // "Original English: Reload App"
    new_window: 'Nytt vindauge', // "Original English: New Window"
    open_trash: 'Opne papirkorg', // "Original English: Open Trash"
    pick_name_for_worker: 'Vel eit namn for workeren din:', // "Original English: Pick a name for your worker:"
    publish_as_serverless_worker: 'Publiser som Worker', // "Original English: Publish as Worker - 'Worker' kept as technical term"
    'toolbar.enter_fullscreen': 'Gå til fullskjerm', // "Original English: Enter Full Screen"
    'toolbar.github': 'GitHub', // "Original English: GitHub - Brand name kept unchanged"
    'toolbar.refer': 'Verv', // "Original English: Refer - 'Verv' is more natural than 'Referer'"
    'toolbar.save_account': 'Lagre konto', // "Original English: Save Account"
    'toolbar.search': 'Søk', // "Original English: Search"
    'toolbar.qrcode': 'QR-kode', // "Original English: QR Code - Technical standard, adapted to Norwegian"
    used_of: '{{used}} brukt av {{available}}', // "Original English: {{used}} used of {{available}} - Variables preserved in original format"
    worker: 'Worker', // "Original English: Worker - Technical term kept"
    'billing.offering.basic': 'Grunnleggjande', // "Original English: Basic"
    too_many_attempts: 'For mange forsøk. Prøv igjen seinare.', // "Original English: Too many attempts. Please try again later."
    server_timeout: 'Tenaren brukte for lang tid på å svare. Prøv igjen.', // "Original English: The server took too long to respond. Please try again."
    signup_error: 'Det oppstod ein feil under registrering. Prøv igjen.', // "Original English: An error occurred during signup. Please try again."
    welcome_title: 'Velkomen til din personlege internett-datamaskin', // "Original English: Welcome to your Personal Internet Computer"
    welcome_description:
      'Lagre filer, spel spel, finn flotte appar og mykje meir! Alt på ein stad, tilgjengeleg frå kvar som helst når som helst.', // "Original English: Store files, play games, find awesome apps, and much more! All in one place, accessible from anywhere at any time."
    welcome_get_started: 'Kom i gang', // "Original English: Get Started"
    welcome_terms: 'Vilkår', // "Original English: Terms"
    welcome_privacy: 'Personvern', // "Original English: Privacy"
    welcome_developers: 'Utviklarar', // "Original English: Developers"
    welcome_open_source: 'Open kjeldekode', // "Original English: Open Source"
    welcome_instant_login_title: 'Øyeblikkelig innlogging!', // "Original English: Instant Login!"
    alert_error_title: 'Feil!', // "Original English: Error!"
    alert_warning_title: 'Åtvaring!', // "Original English: Warning!"
    alert_info_title: 'Info', // "Original English: Info"
    alert_success_title: 'Vellykka!', // "Original English: Success!"
    alert_confirm_title: 'Er du sikker?', // "Original English: Are you sure?"
    alert_yes: 'Ja', // "Original English: Yes"
    alert_no: 'Nei', // "Original English: No"
    alert_retry: 'Prøv igjen', // "Original English: Retry"
    alert_cancel: 'Avbryt', // "Original English: Cancel"
    signup_confirm_password: 'Stadfest passord', // "Original English: Confirm Password"
    login_email_username_required: 'E-post eller brukarnamn er påkravd', // "Original English: Email or username is required"
    login_password_required: 'Passord er påkravd', // "Original English: Password is required"
    window_title_open: 'Opne', // "Original English: Open"
    window_title_change_password: 'Endre passord', // "Original English: Change Password"
    window_title_select_font: 'Vel skrifttype…', // "Original English: Select font… - Ellipsis preserved"
    window_title_session_list: 'Øktliste!', // "Original English: Session List!"
    window_title_set_new_password: 'Vel nytt passord', // "Original English: Set New Password"
    window_title_instant_login: 'Øyeblikkelig innlogging!', // "Original English: Instant Login!"
    window_title_publish_website: 'Publiser nettside', // "Original English: Publish Website"
    window_title_publish_worker: 'Publiser Worker', // "Original English: Publish Worker"
    window_title_authenticating: 'Autentiserer...', // "Original English: Authenticating..."
    window_title_refer_friend: 'Verv ein ven!', // "Original English: Refer a friend!"
    desktop_show_desktop: 'Vis skrivebord', // "Original English: Show Desktop"
    desktop_show_open_windows: 'Vis opne vindauge', // "Original English: Show Open Windows"
    desktop_exit_full_screen: 'Avslutt fullskjerm', // "Original English: Exit Full Screen"
    desktop_enter_full_screen: 'Gå til fullskjerm', // "Original English: Enter Full Screen"
    desktop_position: 'Posisjon', // "Original English: Position"
    desktop_position_left: 'Venstre', // "Original English: Left"
    desktop_position_bottom: 'Botn', // "Original English: Bottom"
    desktop_position_right: 'Høgre', // "Original English: Right"
    item_shared_with_you: 'Ein brukar har delt denne tingen med deg.', // "Original English: A user has shared this item with you."
    item_shared_by_you: 'Du har delt denne tingen med minst ein annan brukar.', // "Original English: You have shared this item with at least one other user."
    item_shortcut: 'Snarveg', // "Original English: Shortcut"
    item_associated_websites: 'Tilknytt nettside', // "Original English: Associated website"
    item_associated_websites_plural: 'Tilknytte nettsider', // "Original English: Associated websites"
    no_suitable_apps_found: 'Fann ingen passande appar', // "Original English: No suitable apps found"
    window_click_to_go_back: 'Klikk for å gå tilbake.', // "Original English: Click to go back."
    window_click_to_go_forward: 'Klikk for å gå framover.', // "Original English: Click to go forward."
    window_click_to_go_up: 'Klikk for å gå eitt nivå opp.', // "Original English: Click to go one directory up."
    window_title_public: 'Offentleg', // "Original English: Public"
    window_title_videos: 'Videoar', // "Original English: Videos"
    window_title_pictures: 'Bilete', // "Original English: Pictures"
    window_title_puter: 'Puter', // "Original English: Puter - Brand name kept unchanged"
    window_folder_empty: 'Denne mappa er tom', // "Original English: This folder is empty"
    manage_your_subdomains: 'Administrer underdomene dine', // "Original English: Manage Your Subdomains"
    open_containing_folder: 'Opne innhaldsmappa', // "Original English: Open Containing Folder"
  },
}

export default nn
